import { Suspense, useEffect, useState } from "react";
import { useToastContext } from "../../../../../common/components/ui/toast/toast.context";
import {
  TableFilters,
  TableFiltersState,
} from "../../../../../common/components/ui/table/TableFilters";
import Loading from "../../../../../common/components/ui/loading/Loading";
import GenericTable from "../../../../../common/components/ui/table/GenericTable";
import TableFooter from "../../../../../common/components/ui/table/TableFooter";
import {
  ITrafficFine,
  StatusCounterFinesReport,
  TRAFFICFINE_STATUS,
} from "../../../../../types/trafficfine.type";
import {
  chargeTrafficFine,
  getTrafficFines,
} from "../../../../../common/services/trafficfines.api";
import { formatDate } from "../../../../../common/helpers/date.helpers";
import { numberToCurrency } from "../../../../../common/helpers/currency.helpers";
import { IVehicle } from "../../../../../types/vehicle.type";
import { useNavigate } from "react-router-dom";
import { FaCheckCircle, FaTimesCircle } from "react-icons/fa";
import { CHARGE_STATUS } from "../../../../../types/charge.type";
import { Button } from "flowbite-react";
import { useCompanyStore } from "@/common/states/store/Company";

const STATUS_TRAFFICFINE = {
  [TRAFFICFINE_STATUS.PAID]: "Paga",
  [TRAFFICFINE_STATUS.OPEN]: "Aberta",
  [TRAFFICFINE_STATUS.OVERDUE]: "Vencida",
};
const SEVERITY_TRANSLATION = {
  LIGHT: "Leve",
  MEDIUM: "Média",
  SEVERE: "Grave",
  VERY_SEVERE: "Gravíssima",
};

const STATUS_TRAFFICFINE_COLORS = {
  [TRAFFICFINE_STATUS.PAID]: "bg-greenPrimary text-white",
  [TRAFFICFINE_STATUS.OPEN]: "bg-bluePrimary text-white",
  [TRAFFICFINE_STATUS.OVERDUE]: "bg-redPrimary text-white",
};

const STATUS_CHARGE = {
  [CHARGE_STATUS.RECEIVED]: "Paga",
  [CHARGE_STATUS.PENDING]: "Pendente",
  [CHARGE_STATUS.OVERDUE]: "Vencida",
  [CHARGE_STATUS.NOT_CHARGED]: "Não cobrada",
  [CHARGE_STATUS.RENEGOTIATED]: "Renegociada",
};

const STATUS_CHARGE_COLORS = {
  [CHARGE_STATUS.RECEIVED]: "bg-greenPrimary text-white",
  [CHARGE_STATUS.PENDING]: "bg-bluePrimary text-white",
  [CHARGE_STATUS.OVERDUE]: "bg-redPrimary text-white",
  [CHARGE_STATUS.NOT_CHARGED]: "bg-gray-300 text-black",
  [CHARGE_STATUS.RENEGOTIATED]: "bg-yellowPrimary text-white",
};

function getStatusTrafficFine(status?: TRAFFICFINE_STATUS) {
  if (!status || !(status in STATUS_TRAFFICFINE)) {
    return (
      <span className="px-2 p-1 rounded-md bg-gray-300 text-black">
        Desconhecido
      </span>
    );
  }
  return (
    <span
      className={`px-2 p-1 rounded-md ${STATUS_TRAFFICFINE_COLORS[status]}`}
    >
      {STATUS_TRAFFICFINE[status]}
    </span>
  );
}

function getStatusCharge(status?: CHARGE_STATUS) {
  if (!status || !(status in STATUS_CHARGE)) {
    return (
      <span className="px-2 p-1 rounded-md bg-gray-300 text-black">
        Desconhecido
      </span>
    );
  }
  return (
    <span className={`px-2 p-1 rounded-md ${STATUS_CHARGE_COLORS[status]}`}>
      {STATUS_CHARGE[status]}
    </span>
  );
}

/** Auto-refresh report data while this screen is open (1 h). Skips when the browser tab is hidden. */
const TRAFFIC_FINES_AUTO_REFRESH_MS = 60 * 60 * 1000;

const headers = {
  createdAt: "Data de Criação",
  date: "Data da Infração",
  licensePlate: "Placa",
  partner: "Parceiro",
  renter: "Cliente",
  trafficTicket: "Infração",
  value: "Valor",
  gravity: "Gravidade",
  details: "Venc. Infração",
  status: "Status Infração",
  dueDateCharge: "Venc. Cobrança",
  statusCharge: "Status Cobrança",
};

function TableTrafficFines() {
  const navigate = useNavigate();
  const { notifyError, notifySuccess } = useToastContext();
  const [page, setPage] = useState<number>(1);
  const [trafficFines, setTrafficFines] = useState<ITrafficFine[]>([]);
  const [data, setData] = useState({ itemsPerPage: 10, totalItems: 0 });
  const companyStore = useCompanyStore();

  const [statusCounter, setStatusCounter] = useState<StatusCounterFinesReport>({
    paidFines: 0,
    openFines: 0,
    expiredFines: 0,
    paidCharges: 0,
    openCharges: 0,
    expiredCharges: 0,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [autoRefreshToken, setAutoRefreshToken] = useState(0);
  const [filters, setFilters] = useState<TableFiltersState>({
    search: "",
    status: [],
    orderBy: "-date",
  });

  const handleFilterChange = (newFilters: Partial<TableFiltersState>) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      ...newFilters,
    }));
  };

  useEffect(() => {
    async function loadTrafficFines(filters, page) {
      setIsLoading(true);
      const result = await getTrafficFines({ ...filters, page });
      const statusCounterAll: StatusCounterFinesReport = {
        paidFines: 0,
        openFines: 0,
        expiredFines: 0,
        paidCharges: 0,
        openCharges: 0,
        expiredCharges: 0,
      };

      if (
        result["statusInfracoes"] &&
        Array.isArray(result["statusInfracoes"])
      ) {
        result["statusInfracoes"].forEach((item) => {
          const status = item["status"];
          if (status === TRAFFICFINE_STATUS.PAID) {
            statusCounterAll.paidFines = item.totalValue || 0;
          } else if (status === TRAFFICFINE_STATUS.OPEN) {
            statusCounterAll.openFines = item.totalValue || 0;
          } else if (status === TRAFFICFINE_STATUS.OVERDUE) {
            statusCounterAll.expiredFines = item.totalValue || 0;
          }
        });
      }
      if (result["statusCharge"]) {
        if (!Array.isArray(result["statusCharge"])) {
          Object.entries(result["statusCharge"]).forEach(([status, data]) => {
            const statusData = data as { count: number; totalValue: number };
            if (status === CHARGE_STATUS.RECEIVED) {
              statusCounterAll.paidCharges = statusData.totalValue || 0;
            } else if (status === CHARGE_STATUS.PENDING) {
              statusCounterAll.openCharges = statusData.totalValue || 0;
            } else if (status === CHARGE_STATUS.OVERDUE) {
              statusCounterAll.expiredCharges = statusData.totalValue || 0;
            }
          });
        } else {
          result["statusCharge"].forEach((item) => {
            if (item.status === CHARGE_STATUS.RECEIVED) {
              statusCounterAll.paidCharges = item.totalValue || 0;
            } else if (item.status === CHARGE_STATUS.PENDING) {
              statusCounterAll.openCharges = item.totalValue || 0;
            } else if (item.status === CHARGE_STATUS.OVERDUE) {
              statusCounterAll.expiredCharges = item.totalValue || 0;
            }
          });
        }
      }
      setStatusCounter(statusCounterAll);
      setData({
        itemsPerPage: data.itemsPerPage,
        totalItems: result.totalItems,
      });

      if (!result.ok) {
        notifyError(
          (result as { message?: string })?.message || "unkown error"
        );
        return;
      }
      setIsLoading(false);
      setTrafficFines(result.data as ITrafficFine[]);
    }
    loadTrafficFines(filters, page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, page, companyStore, autoRefreshToken]);

  useEffect(() => {
    const id = window.setInterval(() => {
      if (document.visibilityState !== "visible") return;
      setAutoRefreshToken((t) => t + 1);
    }, TRAFFIC_FINES_AUTO_REFRESH_MS);
    return () => window.clearInterval(id);
  }, []);

  async function handleGenerateCharge(trafficTicket) {
    if (!trafficTicket) return;
    setIsLoading(true);
    try {
      const response = await chargeTrafficFine(trafficTicket);
      if (response.ok) {
        notifySuccess("Cobrança gerada com sucesso");
      }
    } catch (error) {
      notifyError("Erro ao gerar cobrança");
    } finally {
      setFilters({ ...filters });
    }
  }

  return (
    <Suspense fallback={<Loading />}>
      <div className="table-section">
        <TableFilters
          dashboardHeader="Relatório de Multas"
          placeholder="Digite a placa ou a infração."
          statusOptions={STATUS_TRAFFICFINE}
          onNewItemClick={() => {}}
          onSearch={handleFilterChange}
          tableData={trafficFines}
          entity="trafficfines"
        />
        <div className="flex flex-col items-center mb-4 gap-3">
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 w-full">
            <div className="bg-[rgb(228,65,49)] rounded-lg shadow-md p-3 flex items-center justify-between hover:shadow-lg transition-shadow">
              <h2 className="text-xs sm:text-sm text-white uppercase font-medium">
                Infrações pagas
              </h2>
              <p className="text-base sm:text-lg text-white font-bold">
                {numberToCurrency(statusCounter.paidFines)}
              </p>
            </div>
            <div className="bg-[rgb(228,65,49)] rounded-lg shadow-md p-3 flex items-center justify-between hover:shadow-lg transition-shadow">
              <h2 className="text-xs sm:text-sm text-white uppercase font-medium">
                Infrações abertas
              </h2>
              <p className="text-base sm:text-lg text-white font-bold">
                {numberToCurrency(statusCounter.openFines)}
              </p>
            </div>
            <div className="bg-[rgb(228,65,49)] rounded-lg shadow-md p-3 flex items-center justify-between hover:shadow-lg transition-shadow">
              <h2 className="text-xs sm:text-sm text-white uppercase font-medium">
                Infrações vencidas
              </h2>
              <p className="text-base sm:text-lg text-white font-bold">
                {numberToCurrency(statusCounter.expiredFines)}
              </p>
            </div>
            <div className="bg-[rgb(228,65,49)] rounded-lg shadow-md p-3 flex items-center justify-between hover:shadow-lg transition-shadow">
              <h2 className="text-xs sm:text-sm text-white uppercase font-medium">
                Cobranças recebidas
              </h2>
              <p className="text-base sm:text-lg text-white font-bold">
                {numberToCurrency(statusCounter.paidCharges)}
              </p>
            </div>
            <div className="bg-[rgb(228,65,49)] rounded-lg shadow-md p-3 flex items-center justify-between hover:shadow-lg transition-shadow">
              <h2 className="text-xs sm:text-sm text-white uppercase font-medium">
                Cobranças abertas
              </h2>
              <p className="text-base sm:text-lg text-white font-bold">
                {numberToCurrency(statusCounter.openCharges)}
              </p>
            </div>
            <div className="bg-[rgb(228,65,49)] rounded-lg shadow-md p-3 flex items-center justify-between hover:shadow-lg transition-shadow">
              <h2 className="text-xs sm:text-sm text-white uppercase font-medium">
                Cobranças vencidas
              </h2>
              <p className="text-base sm:text-lg text-white font-bold">
                {numberToCurrency(statusCounter.expiredCharges)}
              </p>
            </div>
          </div>
        </div>
        <GenericTable
          headers={headers}
          data={trafficFines}
          renders={
            {
              createdAt: formatDate,
              licensePlate: (licensePlate, vehicle) => (
                <a
                  href={`/veiculos/${(vehicle as IVehicle).licensePlate}`}
                  onClick={(e) => {
                    e.preventDefault();
                    navigate(`/veiculos/${(vehicle as IVehicle).licensePlate}`);
                  }}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {licensePlate as string}
                </a>
              ),
              partner: (partner) => {
                const hasManagementTrafficFines = Boolean(
                  partner?.managementTrafficfines
                );

                return (
                  <span
                    className="flex items-center gap-2"
                    style={{ fontSize: "15px" }}
                  >
                    {hasManagementTrafficFines ? (
                      <FaCheckCircle className="text-greenPrimary" size={14} />
                    ) : (
                      <FaTimesCircle className="text-redPrimary" size={14} />
                    )}
                    {partner.name}
                  </span>
                );
              },
              renter: (renter) => {
                return (
                  <span
                    className="flex items-center gap-2"
                    style={{ fontSize: "15px" }}
                  >
                    <a
                      style={{ fontSize: "15px", textAlign: "left" }}
                      href={`/clientes/${renter.id}`}
                      onClick={(e) => {
                        e.preventDefault();
                        navigate(`/clientes/${renter.id}`);
                      }}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {renter.name}
                    </a>
                  </span>
                );
              },
              date: formatDate,
              status: getStatusTrafficFine,
              value: numberToCurrency,
              gravity: (_, data) => {
                const severityKey =
                  data?.gravity as keyof typeof SEVERITY_TRANSLATION;
                return SEVERITY_TRANSLATION[severityKey] || "-";
              },
              details: (details) => {
                const dueDate = details.data_vencimento;
                return dueDate ? dueDate : "-";
              },
              trafficTicket: (trafficTicket, data) => {
                const trafficFinePayment =
                  !!data.attached &&
                  !!data.details?.barcode &&
                  !!data.payment?.billId;

                return (
                  <span
                    className="flex items-center gap-2"
                    style={{ fontSize: "15px" }}
                  >
                    {trafficFinePayment ? (
                      <FaCheckCircle className="text-greenPrimary" size={14} />
                    ) : (
                      <FaTimesCircle className="text-redPrimary" size={14} />
                    )}
                    <a
                      href={`/relatorios/multas/${trafficTicket}`}
                      onClick={(e) => {
                        e.preventDefault();
                        navigate(`/relatorios/multas/${trafficTicket}`);
                      }}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {trafficTicket}
                    </a>
                  </span>
                );
              },
              dueDateCharge: (dueDateCharge, data) => {
                if (data.statusCharge === CHARGE_STATUS.RENEGOTIATED)
                  return "-";
                const isValidDate =
                  dueDateCharge && !isNaN(new Date(dueDateCharge).getTime());
                return isValidDate ? formatDate(dueDateCharge) : "-";
              },
              statusCharge: (statusCharge, trafficTicket) => {
                if (
                  !statusCharge ||
                  statusCharge === CHARGE_STATUS.NOT_CHARGED
                ) {
                  return (
                    <div className="flex justify-center">
                      <Button
                        onClick={() =>
                          handleGenerateCharge(trafficTicket.trafficTicket)
                        }
                        className="text-white rounded-md"
                        style={{
                          backgroundColor: "redPrimary",
                          cursor: "pointer",
                          width: "140px",
                          fontSize: "10px",
                        }}
                      >
                        Gerar Cobrança
                      </Button>
                    </div>
                  );
                } else {
                  return (
                    <div className="flex justify-center">
                      {getStatusCharge(statusCharge)}
                    </div>
                  );
                }
              },
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
            } as any
          }
        />

        {isLoading && <Loading />}

        <TableFooter
          page={page || 1}
          totalItems={data.totalItems || 0}
          itemsPerPage={+(filters.itemsPerPage ?? 10)}
          goToPage={setPage}
        />
      </div>
    </Suspense>
  );
}

export default TableTrafficFines;
